"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Global page-transition loader.
 * - Listens for clicks on internal <a> tags (NOT popstate — back-nav uses
 *   Next.js router cache and resolves instantly, so the spinner would hang)
 * - Shows the spinning volleyball overlay after a short grace period
 * - Clears automatically when usePathname() fires (navigation complete)
 * - Safety timeout clears the overlay even if pathname never changes
 *
 * 2026-07-28 hardening — this overlay is `fixed inset-0 z-[9999]`, so while
 * it is up it swallows EVERY click on the page. Combined with a route segment
 * that has no loading.tsx (pathname commits late), an athlete could sit in
 * front of a dead screen for the full safety timeout, clicking a button that
 * never responded. That is what the "unresponsive Vídeo destaque editor"
 * session replay was. Three changes:
 *   1. GRACE_MS — don't paint at all for fast navigations (most of them).
 *      No overlay, no swallowed clicks, no dark flash.
 *   2. SAFETY_MS cut from 8s to 4s — the stuck window is half as long.
 *   3. The overlay is dismissible. Clicking/tapping it hides it immediately
 *      (the pending navigation keeps resolving underneath). A user who thinks
 *      the page is frozen now taps their way out instead of being trapped.
 * The real fix is still a loading.tsx per route segment — this is the net.
 */

const GRACE_MS = 180;
const SAFETY_MS = 4000;

export function NavigationLoader() {
  const [loading, setLoading] = useState(false);
  const pathname = usePathname();
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearTimers() {
    if (graceTimer.current) {
      clearTimeout(graceTimer.current);
      graceTimer.current = null;
    }
    if (safetyTimer.current) {
      clearTimeout(safetyTimer.current);
      safetyTimer.current = null;
    }
  }

  // Clear as soon as the new page is ready
  useEffect(() => {
    setLoading(false);
    clearTimers();
  }, [pathname]);

  // Intercept internal link clicks only (no popstate)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Skip if any modifier (cmd/ctrl-click, middle-click etc — those open in new tab)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;
      // Skip external, hash-only, mailto, downloads, and new-tab links
      if (
        href.startsWith("http") ||
        href.startsWith("//") ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        anchor.hasAttribute("download") ||
        anchor.getAttribute("target") === "_blank"
      ) return;

      // Skip same-pathname clicks — pathname won't change so the clearing
      // useEffect would never fire and the spinner would hang forever.
      // Strip query/hash before comparing.
      const destPath = href.split("?")[0].split("#")[0];
      if (destPath === window.location.pathname) return;

      clearTimers();

      // Grace period: most navigations resolve well inside GRACE_MS, and an
      // overlay that never paints can never eat a click.
      graceTimer.current = setTimeout(() => {
        setLoading(true);
        graceTimer.current = null;
      }, GRACE_MS);

      // Safety timeout — clear no matter what so a slow server or a cancelled
      // navigation can never strand the overlay.
      safetyTimer.current = setTimeout(() => {
        setLoading(false);
        clearTimers();
      }, GRACE_MS + SAFETY_MS);
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      clearTimers();
    };
  }, []);

  if (!loading) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Carregando"
      // Tap-to-dismiss escape hatch — see the note at the top of this file.
      onClick={() => { setLoading(false); clearTimers(); }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4"
      style={{ background: "rgba(8, 15, 24, 0.85)", backdropFilter: "blur(4px)", cursor: "pointer" }}
    >
      <img
        src="/vb_favicon_ball.png"
        alt=""
        width={56}
        height={56}
        className="animate-spin opacity-90"
        style={{ animationDuration: "1.2s" }}
      />
      <p className="text-white/30 text-sm tracking-wide">Carregando…</p>
    </div>
  );
}
